import { useState, useEffect, useCallback, memo, useRef } from 'react';
import {
  Link,
  useFetcher,
  useNavigate,
  useParams,
  useMatches,
} from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  styled,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  useTheme,
  Autocomplete,
  ListItemText,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { MemberRepository } from 'src/api/member_repository';
import { InvitationErrorPopup } from 'src/components/invitation_error_popup';
import { InvitationList } from 'src/components/invitation_list';
import { MemberList } from 'src/components/member_list';
import { PageHeader } from 'src/components/page_header';
import { PageTitle } from 'src/components/page_title';
import { getPlatform, getRegion } from 'src/config/environment';
import { useDebounce } from 'src/hooks/use_debounce';
import { RoleFieldsFragment } from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { InvitationErrorDetails } from 'src/types/errors';
import { MemberAction } from 'src/types/loaders';
import { InvitationInput, InviteRow } from 'src/types/member_management';
import { UserRole } from 'src/types/user_roles';
import {
  notifySuccess,
  getRoleDisplayName,
  getRoleDescription,
} from 'src/utils';
import { isValidEmail } from 'src/utils';
import { getEnumValue } from 'src/utils/enum_utils';

const DUPLICATE_EMAIL_ERROR = 'Email must be unique';

const MemberManagementContainer = styled(Box)({
  alignSelf: 'center',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'min(100%, 1000px)',
  maxWidth: '2000px',
  minHeight: 0,
});

const TabContentContainer = styled(Box)({
  marginTop: '24px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0, // Allow flexbox to shrink
});

const DeleteRowButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
  width: 36,
  height: 36,
}));

// Memoized invite row component to prevent unnecessary re-renders from the parent's state changes.
const InviteRowComponent = memo<{
  invite: InviteRow;
  index: number;
  canSelectMultipleRoles: boolean;
  availableRoles: RoleFieldsFragment[];
  onEmailChange: (index: number, value: string) => void;
  onRoleChange: (
    index: number,
    roles: RoleFieldsFragment | RoleFieldsFragment[] | null,
  ) => void;
  onDeleteRow: (index: number) => void;
  showDeleteButton: boolean;
}>(
  ({
    invite,
    index,
    canSelectMultipleRoles,
    availableRoles,
    onEmailChange,
    onRoleChange,
    onDeleteRow,
    showDeleteButton,
  }) => {
    // Local state for email input to prevent large rerenders of the parent component.
    const [email, setEmail] = useState(invite.email);

    // Sync email state with prop changes (in case parent resets it)
    useEffect(() => {
      setEmail(invite.email);
    }, [invite.email]);

    const debouncedEmailChange = useDebounce((value: string) => {
      onEmailChange(index, value);
    }, 200);

    const handleEmailInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        debouncedEmailChange(value);
      },
      [debouncedEmailChange],
    );

    return (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 1,
        }}
      >
        <TextField
          label='Email'
          placeholder="Person's email you are inviting"
          value={email}
          onChange={handleEmailInputChange}
          variant='outlined'
          fullWidth
          error={!!invite.errors.email}
          helperText={invite.errors.email || ' '}
          slotProps={{
            htmlInput: {
              'data-testid': `email-input-${index}`,
            },
          }}
        />
        <Autocomplete
          id={`roles-autocomplete-${index}`}
          multiple={canSelectMultipleRoles}
          limitTags={1}
          options={availableRoles}
          value={
            canSelectMultipleRoles
              ? invite.roles
              : invite.roles.length > 0
                ? invite.roles[0]
                : null
          }
          onChange={(_, newRoles) => {
            onRoleChange(index, newRoles);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterSelectedOptions
          sx={{
            flex: '0 0 350px',
            minWidth: '350px',
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Role(s)'
              placeholder={invite.roles.length === 0 ? "Person's Role(s)" : ''}
              error={!!invite.errors.role}
              helperText={invite.errors.role || ' '}
              slotProps={{
                ...params.slotProps,
                htmlInput: {
                  ...params.slotProps?.htmlInput,
                  'data-testid': `role-input-${index}`,
                },
              }}
            />
          )}
          renderOption={(props, option: RoleFieldsFragment) => {
            const { key: _key, ...otherProps } = props;
            const userRole = getEnumValue(UserRole, option.name);
            return (
              <li {...otherProps} key={option.id}>
                <ListItemText
                  primary={getRoleDisplayName(
                    userRole,
                    getPlatform(),
                    getRegion(),
                  )}
                  secondary={getRoleDescription(userRole, getPlatform())}
                />
              </li>
            );
          }}
          getOptionLabel={(option: RoleFieldsFragment) =>
            getRoleDisplayName(
              getEnumValue(UserRole, option.name),
              getPlatform(),
              getRegion(),
            )
          }
          getOptionKey={(option: RoleFieldsFragment) => option.id}
        />
        {showDeleteButton && (
          <DeleteRowButton
            onClick={() => onDeleteRow(index)}
            sx={{ mt: 1, mr: 2 }}
            data-testid={`delete-row-button-${index}`}
          >
            <DeleteIcon fontSize='small' />
          </DeleteRowButton>
        )}
      </Box>
    );
  },
);

export const MemberManagementPage: React.FC<{
  initialTabIndex?: number;
  memberRepository: MemberRepository;
  isBackgroundMode?: boolean;
}> = ({ initialTabIndex = 0, memberRepository, isBackgroundMode = false }) => {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(initialTabIndex);
  const navigate = useNavigate();
  const matches = useMatches();
  const { organization } = useParams();

  // Determine which component to render based on current route match or initialTabIndex in background mode
  const hasMatchWithId = (id: string) =>
    matches.some((match) => match.id === id);

  // In background mode, use initialTabIndex to determine which tab to show
  // In normal mode, use route matches
  const isOnMembersRoute = isBackgroundMode
    ? initialTabIndex === 0
    : hasMatchWithId('members');
  const isOnInvitesRoute = isBackgroundMode
    ? initialTabIndex === 1
    : hasMatchWithId('invites');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [invites, setInvites] = useState<InviteRow[]>([
    { email: '', roles: [], errors: { email: '', role: '' } },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitationErrors, setInvitationErrors] = useState<
    InvitationErrorDetails[]
  >([]);
  const [successfulInvitations, setSuccessfulInvitations] = useState<string[]>(
    [],
  );
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const fetcher = useFetcher();
  const canSelectMultipleRoles = getPlatform() === DistributionPlatform.Samd;
  const lastProcessedFetcherDataRef = useRef<unknown>(undefined);

  useEffect(() => {
    setTabIndex(initialTabIndex);
  }, [initialTabIndex]);

  // Handle invitation results (only once per submission to avoid duplicate toasts)
  useEffect(() => {
    if (!fetcher.data || fetcher.state !== 'idle') return;
    if (lastProcessedFetcherDataRef.current === fetcher.data) return;
    lastProcessedFetcherDataRef.current = fetcher.data;

    const actionResult = fetcher.data;

    if (actionResult.action === MemberAction.INVITE_USERS) {
      if (actionResult.success) {
        notifySuccess('All invitations sent successfully.');
        if (isOnMembersRoute) {
          navigate(`/${organization}/invites`);
        }
      } else if (
        actionResult.invitationErrors &&
        actionResult.invitationErrors.length > 0
      ) {
        setInvitationErrors(actionResult.invitationErrors);
        setSuccessfulInvitations(actionResult.successfulInvitations || []);
        setShowErrorPopup(true);
      }
    }
  }, [fetcher.data, fetcher.state, isOnMembersRoute, navigate, organization]);

  // Open the invite dialog and initialize the first invite and error objects
  const handleInviteMembers = () => {
    setInviteDialogOpen(true);
    setInvites([{ email: '', roles: [], errors: { email: '', role: '' } }]);
  };

  const handleCloseDialog = () => {
    setInviteDialogOpen(false);
  };

  // Add a new invite row with empty email and roles and an empty error object for validation
  const handleAddMore = useCallback(() => {
    setInvites((prevInvites) => [
      ...prevInvites,
      { email: '', roles: [], errors: { email: '', role: '' } },
    ]);
  }, []);

  // Helper function to clear duplicate-specific errors while preserving other validation errors
  const clearDuplicateErrorIfPresent = (
    invite: InviteRow,
    index: number,
    updatedInvites: InviteRow[],
  ): void => {
    if (invite.errors.email === DUPLICATE_EMAIL_ERROR) {
      updatedInvites[index] = {
        ...invite,
        errors: { ...invite.errors, email: '' },
      };
    }
  };

  // Helper function to validate and update duplicate email errors
  const validateDuplicateEmails = useCallback(
    (invites: InviteRow[]): InviteRow[] => {
      const emailCount = new Map<string, number>();
      const updatedInvites = [...invites];

      // First pass: count occurrences of each email
      updatedInvites.forEach((invite) => {
        if (invite.email.trim()) {
          const emailLower = invite.email.toLowerCase();
          emailCount.set(emailLower, (emailCount.get(emailLower) || 0) + 1);
        }
      });

      // Second pass: mark duplicates (but not the first occurrence)
      const firstOccurrence = new Set<string>();
      updatedInvites.forEach((invite, index) => {
        if (invite.email.trim()) {
          const emailLower = invite.email.toLowerCase();
          const count = emailCount.get(emailLower) || 0;

          if (count > 1) {
            // This email has duplicates
            if (firstOccurrence.has(emailLower)) {
              // This is a duplicate, mark it
              updatedInvites[index] = {
                ...invite,
                errors: { ...invite.errors, email: DUPLICATE_EMAIL_ERROR },
              };
            } else {
              // This is the first occurrence, mark it as seen
              // Only clear duplicate-specific errors, preserve other validation errors
              firstOccurrence.add(emailLower);
              clearDuplicateErrorIfPresent(invite, index, updatedInvites);
            }
          } else {
            // This email is unique, clear only duplicate-specific errors
            // Preserve other validation errors (like "Please enter a valid email")
            clearDuplicateErrorIfPresent(invite, index, updatedInvites);
          }
        } else {
          // Empty email, preserve existing validation errors (like "Email is required")
          // Only clear duplicate-specific errors
          clearDuplicateErrorIfPresent(invite, index, updatedInvites);
        }
      });

      return updatedInvites;
    },
    [],
  );

  const handleEmailChange = useCallback(
    (index: number, value: string) => {
      setInvites((prevInvites) => {
        const updatedInvites = [...prevInvites];
        updatedInvites[index] = {
          ...updatedInvites[index],
          email: value,
          errors: {
            ...updatedInvites[index].errors,
            email: '',
          },
        };

        // Validate for duplicate emails in real-time
        return validateDuplicateEmails(updatedInvites);
      });
    },
    [validateDuplicateEmails],
  );

  const handleRoleChange = useCallback(
    (
      index: number,
      roles: RoleFieldsFragment | RoleFieldsFragment[] | null,
    ) => {
      setInvites((prevInvites) => {
        const updatedInvites = [...prevInvites];
        const newRoles = canSelectMultipleRoles
          ? (roles as RoleFieldsFragment[])
          : roles
            ? [roles as RoleFieldsFragment]
            : [];

        updatedInvites[index] = {
          ...updatedInvites[index],
          roles: newRoles,
          errors: {
            ...updatedInvites[index].errors,
            role: '',
          },
        };
        return updatedInvites;
      });
    },
    [canSelectMultipleRoles],
  );

  const validateForm = (): boolean => {
    let isValid = true;

    const newInvites = invites.map<InviteRow>((invite) => {
      const newInvite = { ...invite, errors: { ...invite.errors } };

      if (!invite.email.trim()) {
        newInvite.errors.email = 'Email is required';
        isValid = false;
      } else if (!isValidEmail(invite.email)) {
        newInvite.errors.email = 'Please enter a valid email';
        isValid = false;
      }

      if (!invite.roles || invite.roles.length === 0) {
        newInvite.errors.role = 'At least one role is required';
        isValid = false;
      }

      return newInvite;
    });

    // Validate for duplicate emails using the helper function
    const validatedInvites = validateDuplicateEmails(newInvites);

    // Check if there are any duplicate errors
    const hasDuplicateErrors = validatedInvites.some(
      (invite) => invite.errors.email === 'Email must be unique',
    );

    if (hasDuplicateErrors) {
      isValid = false;
    }

    setInvites(validatedInvites);
    return isValid;
  };

  const handleSendInvites = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    if (invites.length > 0) {
      const formData = new FormData();
      formData.append('actionType', MemberAction.INVITE_USERS);

      const inviteInputs = invites.map(
        (invite) =>
          new InvitationInput(
            invite.email,
            invite.roles.map((role) => role.id),
          ),
      );
      formData.append('invites', JSON.stringify(inviteInputs));

      await fetcher.submit(formData, {
        method: 'POST',
        action: `/${organization}/invites`,
      });

      handleCloseDialog();
      setTabIndex(1);
    }

    setIsSubmitting(false);
  };

  const handleDeleteRow = useCallback(
    (index: number) => {
      setInvites((prevInvites) => {
        const updatedInvites = [...prevInvites];
        updatedInvites.splice(index, 1);

        // Re-validate for duplicates after deletion
        return validateDuplicateEmails(updatedInvites);
      });
    },
    [validateDuplicateEmails],
  );

  const handleCloseErrorPopup = () => {
    setShowErrorPopup(false);
    setInvitationErrors([]);
    setSuccessfulInvitations([]);
  };

  const { data: availableRoles } = useSuspenseQuery<RoleFieldsFragment[]>({
    queryKey: ['assignableRoles'],
    queryFn: async () => {
      return memberRepository.getAssignableRoles();
    },
    // Prevent refetching when in background mode
    refetchOnMount: !isBackgroundMode,
    refetchOnWindowFocus: !isBackgroundMode,
  });

  return (
    <MemberManagementContainer>
      <PageHeader
        action={
          <Button
            variant='contained'
            color='primary'
            onClick={handleInviteMembers}
            size='large'
            data-testid='invite-members-button'
            sx={{
              whiteSpace: 'nowrap',
              [theme.breakpoints.down('md')]: {
                width: '100%',
                maxWidth: '200px',
              },
            }}
          >
            INVITE MEMBERS
          </Button>
        }
      >
        <PageTitle title='Members Management' />
      </PageHeader>
      <Box>
        <Tabs
          value={tabIndex}
          aria-label='member management tabs'
          sx={{
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: theme.palette.divider,
              zIndex: 0,
            },
            '& .MuiTabs-indicator': {
              display: 'none', // Hide the default blue tab indicator
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: theme.typography.fontWeightMedium,
              minWidth: 'auto',
              paddingX: 2,
              borderTop: '1px solid transparent',
              borderLeft: '1px solid transparent',
              borderRight: '1px solid transparent',
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              marginBottom: '-1px', // Overlap with container border
              position: 'relative',
              zIndex: 1,
            },
            '& .MuiTab-root.Mui-selected': {
              borderTop: `1px solid ${theme.palette.divider}`,
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderRight: `1px solid ${theme.palette.divider}`,
              borderBottom: '1px solid transparent',
              backgroundColor: theme.palette.grey[100],
              zIndex: 2, // Ensure selected tab appears above others
            },
          }}
        >
          <Tab
            component={Link}
            to={`/${organization}/members`}
            label='Members'
            value={0}
            data-testid='members-tab'
          />
          <Tab
            component={Link}
            to={`/${organization}/invites`}
            label='Invitations'
            value={1}
            data-testid='invitations-tab'
          />
        </Tabs>
      </Box>
      <TabContentContainer>
        {isOnMembersRoute ? (
          <MemberList
            key='members'
            memberRepository={memberRepository}
            isBackgroundMode={isBackgroundMode}
          />
        ) : isOnInvitesRoute ? (
          <InvitationList
            key='invitations'
            memberRepository={memberRepository}
            isBackgroundMode={isBackgroundMode}
          />
        ) : null}
      </TabContentContainer>
      <Dialog
        open={inviteDialogOpen}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
        data-testid='invite-dialog'
      >
        <DialogTitle
          sx={{ textAlign: 'left' }}
          data-testid='invite-dialog-title'
        >
          Send invites by email
          <Typography
            variant='body2'
            component='div'
            sx={{
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            You must specify an email address to send invitations.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              maxHeight: 400,
              overflowY: 'auto',
              py: 1,
            }}
          >
            {invites.map((invite, index) => (
              <InviteRowComponent
                key={index}
                invite={invite}
                index={index}
                canSelectMultipleRoles={canSelectMultipleRoles}
                availableRoles={availableRoles}
                onEmailChange={handleEmailChange}
                onRoleChange={handleRoleChange}
                onDeleteRow={handleDeleteRow}
                showDeleteButton={invites.length > 1}
              />
            ))}
          </Box>

          <Button
            startIcon={<AddIcon />}
            size='large'
            onClick={handleAddMore}
            sx={{ color: 'primary.main' }}
            data-testid='add-more-button'
          >
            Add More
          </Button>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={isSubmitting}
            size='large'
            data-testid='cancel-button'
          >
            CANCEL
          </Button>
          <Button
            onClick={handleSendInvites}
            variant='contained'
            color='primary'
            disabled={isSubmitting}
            size='large'
            data-testid='send-invites-button'
          >
            SEND INVITES
          </Button>
        </DialogActions>
      </Dialog>
      <InvitationErrorPopup
        isOpen={showErrorPopup}
        onClose={handleCloseErrorPopup}
        errors={invitationErrors}
        successfulInvitations={successfulInvitations}
      />
    </MemberManagementContainer>
  );
};
